import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationError, Router, RouterEvent, RouterOutlet } from '@angular/router';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

import {
  AccountService,
  LocalizationService,
  RoutesHandlerService,
  ScrollHandlerService,
  StateService,
  UserService,
  OnboardingService,
} from './shared/services';
import { AuthService } from 'src/app/core/services/auth/auth.service';

import { Account, ConditionInfo, HeaderState, Locale, User } from './shared/models';
import { CodesVariableNamesEnum } from './shared/enums';
import { systemLocales } from './shared/config';
import { ScrollbarComponent } from './scrollbar/scrollbar.component';
import { ScrollbarModule } from './scrollbar/scrollbar.module';
import { FooterComponent } from './components/footer/footer.component';
import { AppHandlerService } from './modules/core/services';
import { HintOverlayService } from './shared/services/onboarding/hint-overlay.service';
import { TrialPopupService } from './shared/services/user/trial-popup.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterOutlet, CdkScrollableModule, ScrollbarModule, FooterComponent],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly handler = inject(AppHandlerService);
  private readonly userService = inject(UserService);
  private readonly accountService = inject(AccountService);
  private readonly authService = inject(AuthService);
  private readonly routesHandler = inject(RoutesHandlerService);
  private readonly headerService = inject(StateService);
  private readonly localization = inject(LocalizationService);
  private readonly scrollHandler = inject(ScrollHandlerService);
  private readonly onboardingService = inject(OnboardingService);
  private readonly router = inject(Router);
  private readonly hintOverlayService = inject(HintOverlayService);
  private readonly trialPopupService = inject(TrialPopupService);
  private readonly dialog = inject(MatDialog);

  @ViewChild('scrollbar') scrollbarRef: ScrollbarComponent;

  windowWidth: number;
  isUserLoggedIn: boolean;
  isMainLanding: boolean;

  private user: User;
  private headerState: HeaderState;
  private accountInfo: Account;
  private currentUserInit: User;
  private updateUserSet = false;
  private isUpdateCurrentUserStarted = false;

  private disableScrollBar: boolean = false;

  private unsubscribe$ = new Subject<void>();
  private updateIsRoutesDataInit$ = new BehaviorSubject<boolean>(false);
  private disableScrollBarSubject = new BehaviorSubject<ConditionInfo>({
    condition: this.disableScrollBar,
  });
  private subs = new Subscription();

  isRotesDataInit$: Observable<boolean> = this.updateIsRoutesDataInit$.asObservable();
  systemLocale$: Observable<Locale>;
  disableScrollBar$: Observable<ConditionInfo> = this.disableScrollBarSubject.asObservable();

  get isItLanding(): boolean {
    return this.headerState?.landing || this.headerState?.auth || !this.isUserLoggedIn;
  }

  @HostListener('window:resize') onResize(): void {
    this.windowWidth = this.handler.getWindowWidth();
  }

  @HostListener('window:beforeunload', ['$event'])
  // @ts-ignore
  async ngOnDestroy($event) {
    $event.preventDefault();
    this.resetData();

    this.authService.handleRefreshingOnDestroy();

    this.handler.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.subs.unsubscribe();
  }

  @HostListener('window:storage', ['$event']) onStorage(event: StorageEvent) {
    if (event?.key !== CodesVariableNamesEnum.accessToken) {
      return;
    }

    if (event.newValue) {
      this.authService.handleHeldRequests();
    } else {
      this.authService.fullUserLogout(false);
    }
  }

  ngOnInit(): void {
    this.handler.init();
    this.initWidowWidth();
    this.initUser();
    this.handleRoutesData();
    this.initSubscriptions();
  }

  ngAfterViewInit(): void {
    this.handler.setScrollbarRef(this.scrollbarRef?.element?.nativeElement);
  }

  private initWidowWidth(): void {
    this.windowWidth = this.handler.getWindowWidth();
  }

  private initUser(): void {
    const userId: string = this.authService.getUserId();

    this.localization.setSystemLocale(null, false);
    this.userService.updateCurrentUser(userId);

    this.isUpdateCurrentUserStarted = true;
    this.currentUserInit = null;
  }

  private initSubscriptions(): void {
    this.handleUpdateAccount();
    this.handleLogout();
    this.handleChunkLoadErrors();

    this.setSystemLocalePipe();
    this.handleUpdateState();
    this.onUpdateDisableScrollBar();
    this.initOnboarding();
  }

  private initOnboarding(): void {
    this.userService.currentUser$
      .pipe(
        filter((user: User) => !!user),
        switchMap(() => this.onboardingService.init()),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();
  }

  private onUpdateDisableScrollBar(): void {
    this.scrollHandler.disableScrollBar$
      .pipe(
        filter((info: ConditionInfo) => !!info),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((info: ConditionInfo) => {
        this.updateDisableScrollBar(info.condition);
      });
  }

  private setSystemLocalePipe(): void {
    this.systemLocale$ = this.localization.systemLocale$.pipe(map((locale: Locale) => locale));
  }

  private updateDisableScrollBar(disable: boolean = false): void {
    if (disable !== this.disableScrollBar) {
      this.disableScrollBar = disable;

      this.disableScrollBarSubject.next({ condition: this.disableScrollBar });
    }
  }

  private handleUpdateState(): void {
    this.headerService.updateState$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state: HeaderState) => {
        this.headerState = state;
      });
  }

  private handleCurrentUser(isUpdatingAccount: boolean): void {
    this.userService.currentUser$
      .pipe(
        filter((user: User) => !!user),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((updatedUser: User) => {
        this.user = updatedUser;

        if (
          !this.currentUserInit ||
          this.currentUserInit?.userId !== updatedUser?.userId ||
          isUpdatingAccount
        ) {
          this.currentUserInit = updatedUser;

          this.handleAccountAfterGettingUser();
        }

        this.userService.setUser(this.user);
        this.userService.updateUserLocale(updatedUser);

        this.setPagesTitles();
      });
  }

  private handleRoutesData(): void {
    this.routesHandler
      .getRouterEventsAtTheEnd()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event: RouterEvent) => {
        this.handleRoutesDataEndSuccess(event);
      });
  }

  private handleRoutesDataEndSuccess(event: RouterEvent): void {
    this.preHandleRoutesDataEndSuccess(event);
    this.handleDisablingScrollForPages(event);

    if (
      !this.isUserLoggedIn &&
      (this.routesHandler.getIsAuthPage(null, event) ||
        this.routesHandler.getIsLanding(null, event))
    ) {
      this.handler.clearWidget();
      this.userService.updateUserLocale();
    } else if (systemLocales.some((locale: Locale) => event?.url?.includes(locale))) {
      this.handleSystemOnRoutesChange(event);
    }

    this.setPagesTitles();
  }

  private handleDisablingScrollForPages(event: RouterEvent): void {
    const isSearchPage: boolean = this.routesHandler.getIsSearchState(null, event);
    const isProfilePage: boolean = this.routesHandler.getIsCandidateState(null, event);
    const isAdminPanelPage: boolean = this.routesHandler.getIsAdminPanelState(null, event);
    const isUserState: boolean = this.routesHandler.getIsUserState(null, event);

    this.updateDisableScrollBar(isSearchPage || isProfilePage || isUserState || isAdminPanelPage);
  }

  private preHandleRoutesDataEndSuccess(event: RouterEvent): void {
    this.isMainLanding = this.routesHandler.getIsMainLanding(null, event);
    this.isUserLoggedIn = this.authService.getIsUserLoggedIn();
    const isRotesDataInit = true;

    this.handleState(event);
    this.updateIsRoutesDataInit$.next(isRotesDataInit);
  }

  private handleSystemOnRoutesChange(event: RouterEvent): void {
    const accountCondition: boolean = this.routesHandler.getIsSystemOnRoutesChangeByEvent(event);
    const updateUserCondition: boolean =
      this.routesHandler.getUserChangeOnRoutesChangeByEvent(event);

    this.handler.handleChangedUser(this.user);

    if (accountCondition) {
      this.setAccountInfo();
    }

    if (!this.isUpdateCurrentUserStarted && updateUserCondition) {
      const userId: string = this.authService.getUserId();
      this.isUpdateCurrentUserStarted = true;

      this.userService.updateCurrentUser(userId);
    }
  }

  private setPagesTitles(): void {
    this.routesHandler
      .getPagesTitles()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((resultTitle: string) => {
        this.routesHandler.setTitle(resultTitle);
      });
  }

  private handleState(event: RouterEvent): void {
    const state: string = this.routesHandler.getStateByEvent(event);

    this.headerService.updateState(state);
  }

  private setAccountInfo(isUpdatingAccount: boolean = false, user: User = null): void {
    this.handler
      .getAccount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((account: Account) => {
        this.handleGettingAccountSuccess(account, isUpdatingAccount, user);
      });
  }

  private handleGettingAccountSuccess(
    account: Account,
    isUpdatingAccount: boolean = false,
    user: User = null,
  ): void {
    if (account) {
      this.accountInfo = account;

      if (!this.currentUserInit) {
        this.handleInitUserOnAccount(isUpdatingAccount);
      } else {
        this.accountService.updateAccountInfo(this.accountInfo);
        this.maybeShowTrialPopup(this.accountInfo);
      }

      if (user) {
        this.userService.setUserInfoAndUpdate(user);
      }
    }
  }

  private handleInitUserOnAccount(isUpdatingAccount: boolean): void {
    this.handleCurrentUser(isUpdatingAccount);

    if (!this.isUpdateCurrentUserStarted && !this.user && !this.updateUserSet) {
      const userId: string = this.authService.getUserId();
      this.updateUserSet = true;
      this.isUpdateCurrentUserStarted = true;

      this.userService.updateCurrentUser(userId);
    }
  }

  private handleAccountAfterGettingUser(): void {
    if (this.accountInfo) {
      this.accountService.updateAccountInfo(this.accountInfo);
      this.maybeShowTrialPopup(this.accountInfo);
    }
  }

  private handleUpdateAccount(): void {
    this.accountService.updateAccount$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(({ withMarkers, user }) => {
        this.setAccountInfo(true, user);
      });
  }

  private handleChunkLoadErrors(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationError => event instanceof NavigationError),
        filter((event) => this.isChunkLoadError(event.error)),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((event) => {
        window.location.assign(event.url);
      });
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);

    return (
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      message.includes('ChunkLoadError')
    );
  }

  private handleLogout(): void {
    this.authService.onLogout$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.onboardingService.reset();
      this.hintOverlayService.clearAutoOpened();

      if (this.accountService.getIsAccountBlocked(this.accountInfo)) {
        this.handler.closeAllDialogs();
      }

      this.handler.clearWidget();
      this.resetData();
    });
  }

  private resetData() {
    this.isUserLoggedIn = false;
    this.headerState = null;
    this.accountInfo = null;
    this.user = null;
    this.currentUserInit = null;
  }

  private maybeShowTrialPopup(account: Account): void {
    const milestone = this.trialPopupService.getDueMilestone(account);
    if (!milestone) {
      return;
    }
    this.trialPopupService.markShown(milestone);
    import('./modules/system-shared/components/trial-milestone-modal/trial-milestone-modal.component').then(
      (m) => this.dialog.open(m.TrialMilestoneModalComponent, { data: { milestone } }),
    );
  }
}
